import { useEffect, useMemo, useState } from 'react'
import { collection, onSnapshot, orderBy, query, where } from 'firebase/firestore'
import { db } from '@/firebase'
import { useAuth } from '@/contexts/AuthContext'
import { useBranch } from '@/contexts/BranchContext'
import { roleService } from '@/services/roleService'
import PageHeader from '@/components/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cashUpService, formatCashUpBreakdownText, cashUpBreakdownFromRecord, parseMoneyInput, tillGrandFrom, variance, formatVariance, countedTotalsFromCashUp } from '@/services/cashUpService'
import toast from 'react-hot-toast'
import LoadingSpinner from '@/components/LoadingSpinner'
import PDFGenerator from '@/utils/pdfGenerator'
import { businessProfileService } from '@/services/businessProfileService'
import { BranchService } from '@/services/branchService'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'

function formatMoney(n) {
  return `R${Number(n || 0).toFixed(2)}`
}

function toYMD(d) {
  const date = d instanceof Date ? d : new Date(d)
  return date.toISOString().slice(0, 10)
}

function fromYMD(ymd) {
  const [y, m, d] = String(ymd).split('-').map((x) => Number(x))
  if (!y || !m || !d) return new Date()
  return new Date(y, m - 1, d)
}

function addDays(date, days) {
  const d = new Date(date)
  d.setDate(d.getDate() + days)
  return d
}

function startOfMonth(date) {
  const d = new Date(date)
  d.setDate(1)
  d.setHours(0, 0, 0, 0)
  return d
}

function endOfMonth(date) {
  const d = new Date(date)
  d.setMonth(d.getMonth() + 1, 0)
  d.setHours(23, 59, 59, 999)
  return d
}

const BREAKDOWN_SECTIONS = [
  { key: 'washes', title: 'Washes' },
  { key: 'products', title: 'Products' },
  { key: 'addons', title: 'Add-ons' },
  { key: 'subscriptions', title: 'Subscriptions' },
  { key: 'otherLines', title: 'Other' },
]

function sectionTotals(rows) {
  return (rows || []).reduce(
    (acc, r) => ({
      qty: acc.qty + (Number(r.qty) || 0),
      amount: acc.amount + (Number(r.amount) || 0),
    }),
    { qty: 0, amount: 0 }
  )
}

function formatBreakdownAmount(row) {
  if (row?.amount == null || row.amount === '') return '—'
  const n = Number(row.amount)
  return Number.isFinite(n) ? formatMoney(n) : '—'
}

function SalesBreakdownTables({ breakdown, emptyHint }) {
  const hasAny = BREAKDOWN_SECTIONS.some((s) => (breakdown?.[s.key] || []).length > 0)
  if (!hasAny) {
    return <p className="text-sm text-muted-foreground">{emptyHint || 'No items recorded for this day.'}</p>
  }

  return (
    <div className="space-y-4">
      {BREAKDOWN_SECTIONS.map(({ key, title }) => {
        const rows = breakdown?.[key] || []
        if (!rows.length) return null
        const totals = sectionTotals(rows)
        const hasAmounts = rows.some((r) => r.amount != null && r.amount !== '')
        return (
          <div key={key}>
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{title}</p>
            <div className="mt-1 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border/70 text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                    <th className="py-1.5 pr-2 font-medium">Item</th>
                    <th className="py-1.5 px-2 font-medium text-right w-16">Qty</th>
                    <th className="py-1.5 pl-2 font-medium text-right w-28">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={`${key}-${r.name}`} className="border-b border-border/40">
                      <td className="py-1.5 pr-2 text-foreground">{r.name}</td>
                      <td className="py-1.5 px-2 text-right tabular-nums">{r.qty}</td>
                      <td className="py-1.5 pl-2 text-right tabular-nums">{formatBreakdownAmount(r)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="text-foreground">
                    <td className="py-1.5 pr-2 font-medium">Total</td>
                    <td className="py-1.5 px-2 text-right font-medium tabular-nums">{totals.qty}</td>
                    <td className="py-1.5 pl-2 text-right font-medium tabular-nums">
                      {hasAmounts ? formatMoney(totals.amount) : '—'}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default function CashUpsPage() {
  const { currentUser, userProfile } = useAuth()
  const { currentBranch, isViewingAllBranches } = useBranch()
  const canCashUp = roleService.canUserPerformAction(userProfile, 'perform_cash_up')
  const [cashUps, setCashUps] = useState([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [summaryLoading, setSummaryLoading] = useState(false)
  const [dayTransactions, setDayTransactions] = useState([])
  const [dayWashes, setDayWashes] = useState([])
  const [daySummary, setDaySummary] = useState({
    cashTotal: 0,
    cardTotal: 0,
    eftTotal: 0,
    otherTotal: 0,
    accountTotal: 0,
    accountTransactionCount: 0,
    transactionCount: 0,
  })
  const [activeTab, setActiveTab] = useState('calendar')
  const [calendarMonth, setCalendarMonth] = useState(startOfMonth(new Date()))

  const [form, setForm] = useState({
    date: toYMD(new Date()),
    notes: '',
    emailTo: currentUser?.email || '',
  })
  const [slipTarget, setSlipTarget] = useState(null)
  const [printingSlip, setPrintingSlip] = useState(false)
  const [counted, setCounted] = useState({ cash: '', card: '', eft: '', other: '' })
  const [editingCountedId, setEditingCountedId] = useState(null)
  const [editCounted, setEditCounted] = useState({ cash: '', card: '', eft: '', other: '' })
  const [savingCounted, setSavingCounted] = useState(false)

  useEffect(() => {
    if (!currentUser || !canCashUp) return

    setLoading(true)
    const base = collection(db, 'cashUps')
    const q = isViewingAllBranches()
      ? query(base, orderBy('createdAt', 'desc'))
      : query(base, where('branchId', '==', currentBranch?.id || ''), orderBy('createdAt', 'desc'))

    const unsub = onSnapshot(
      q,
      (snap) => {
        setCashUps(
          snap.docs.map((d) => ({
            id: d.id,
            ...d.data(),
          }))
        )
        setLoading(false)
      },
      (err) => {
        console.error('Error loading cash-ups:', err)
        toast.error('Failed to load cash-ups')
        setLoading(false)
      }
    )

    return () => unsub()
  }, [currentUser, currentBranch?.id, isViewingAllBranches, canCashUp])

  const cashUpsByDate = useMemo(() => {
    const map = new Map()
    cashUps.forEach((c) => {
      if (c.cashUpDate) map.set(c.cashUpDate, c)
    })
    return map
  }, [cashUps])

  const missingLast14Days = useMemo(() => {
    const missing = []
    if (!currentBranch || isViewingAllBranches()) return missing

    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const start = addDays(today, -13)

    for (let d = new Date(start); d <= today; d = addDays(d, 1)) {
      const ymd = toYMD(d)
      const isDone = cashUpsByDate.has(ymd)
      if (!isDone) missing.push(ymd)
    }
    return missing
  }, [cashUpsByDate, currentBranch, isViewingAllBranches])

  useEffect(() => {
    let cancelled = false

    async function loadDaySummary() {
      if (!currentUser || !canCashUp) return
      if (!currentBranch || isViewingAllBranches()) return

      try {
        setSummaryLoading(true)
        const [txns, washes] = await Promise.all([
          cashUpService.getDayTransactions({
            branchId: currentBranch.id,
            date: form.date,
          }),
          cashUpService.getDayWashes({
            branchId: currentBranch.id,
            date: form.date,
          }),
        ])
        if (cancelled) return
        const summary = cashUpService.summarizeTransactions(txns)
        setDayTransactions(txns)
        setDayWashes(washes)
        setDaySummary(summary)
        setCounted({
          cash: Number(summary.cashTotal || 0).toFixed(2),
          card: Number(summary.cardTotal || 0).toFixed(2),
          eft: Number(summary.eftTotal || 0).toFixed(2),
          other: Number(summary.otherTotal || 0).toFixed(2),
        })
      } catch (error) {
        console.error('Error loading cash-up summary:', error)
        if (!cancelled) toast.error('Failed to load today’s POS transactions')
      } finally {
        if (!cancelled) setSummaryLoading(false)
      }
    }

    loadDaySummary()
    return () => {
      cancelled = true
    }
  }, [currentUser, currentBranch?.id, isViewingAllBranches, form.date, canCashUp])

  const grandTotal = useMemo(() => {
    return (
      Number(daySummary.cashTotal || 0) +
      Number(daySummary.cardTotal || 0) +
      Number(daySummary.eftTotal || 0) +
      Number(daySummary.otherTotal || 0)
    )
  }, [daySummary.cashTotal, daySummary.cardTotal, daySummary.eftTotal, daySummary.otherTotal])

  const countedGrand = useMemo(
    () =>
      tillGrandFrom({
        cash: parseMoneyInput(counted.cash),
        card: parseMoneyInput(counted.card),
        eft: parseMoneyInput(counted.eft),
        other: parseMoneyInput(counted.other),
      }),
    [counted]
  )

  function varianceClass(diff) {
    const n = Number(diff || 0)
    if (Math.abs(n) < 0.005) return 'text-muted-foreground'
    return n < 0 ? 'text-destructive' : 'text-emerald-700 dark:text-emerald-400'
  }

  const previewBreakdown = useMemo(
    () => cashUpService.buildSalesBreakdown(dayTransactions, dayWashes),
    [dayTransactions, dayWashes]
  )

  async function createCashUp() {
    if (!currentUser || !canCashUp) return
    if (!currentBranch || isViewingAllBranches()) {
      toast.error('Select a specific branch to cash up')
      return
    }

    try {
      if (cashUpsByDate.has(form.date)) {
        toast.error(`Cash-up already done for ${form.date}`)
        setActiveTab('history')
        return
      }

      setCreating(true)
      const countedPayload = {
        cash: parseMoneyInput(counted.cash),
        card: parseMoneyInput(counted.card),
        eft: parseMoneyInput(counted.eft),
        other: parseMoneyInput(counted.other),
      }

      const result = await cashUpService.createCashUpFromPos({
        branchId: currentBranch.id,
        branchName: currentBranch.name,
        date: form.date,
        createdByUid: currentUser.uid,
        createdByName: userProfile?.name || userProfile?.displayName || currentUser.email,
        createdByEmail: currentUser.email,
        notes: form.notes,
        counted: countedPayload,
      })

      toast.success('Cash-up saved')
      setActiveTab('history')

      const totals = result.totals ?? daySummary
      const breakdown = result.breakdown ?? previewBreakdown
      const txnCount = totals.transactionCount ?? daySummary.transactionCount
      const tillGrand =
        Number(totals.cashTotal || 0) +
        Number(totals.cardTotal || 0) +
        Number(totals.eftTotal || 0) +
        Number(totals.otherTotal || 0)
      const breakdownBlock = formatCashUpBreakdownText({
        washes: breakdown?.washes,
        products: breakdown?.products,
        addons: breakdown?.addons,
        subscriptions: breakdown?.subscriptions,
        otherLines: breakdown?.otherLines,
        accountTotal: totals.accountTotal,
        accountTransactionCount: totals.accountTransactionCount ?? 0,
      })

      const emailTo = (form.emailTo || currentUser.email || '').trim()
      if (emailTo) {
        const countedGrandMail = tillGrandFrom(countedPayload)
        const subject = encodeURIComponent(`Cash-up slip - ${currentBranch.name}`)
        const body = encodeURIComponent(
          `Cash-up saved.\n\nBranch: ${currentBranch.name}\nDate: ${form.date}\nTransactions: ${txnCount}\nPOS grand total: ${formatMoney(tillGrand)}\nCounted grand total: ${formatMoney(countedGrandMail)}\nVariance: ${formatVariance(countedGrandMail - tillGrand)}\n\n${breakdownBlock}\n\nPOS transactions were not changed. Reprint from POS → Cash-ups.\n`
        )
        window.open(`mailto:${encodeURIComponent(emailTo)}?subject=${subject}&body=${body}`)
      }

      setForm((p) => ({
        ...p,
        notes: '',
      }))

      setSlipTarget({
        id: result.id,
        branchId: currentBranch.id,
        branchName: currentBranch.name,
        cashUpDate: form.date,
        createdByName: userProfile?.name || userProfile?.displayName || currentUser.email,
        notes: form.notes,
        cashTotal: totals.cashTotal,
        cardTotal: totals.cardTotal,
        eftTotal: totals.eftTotal,
        otherTotal: totals.otherTotal,
        countedCash: countedPayload.cash,
        countedCard: countedPayload.card,
        countedEft: countedPayload.eft,
        countedOther: countedPayload.other,
        accountTotal: totals.accountTotal,
        accountTransactionCount: totals.accountTransactionCount ?? 0,
        transactionCount: txnCount,
        washBreakdown: breakdown?.washes || [],
        productBreakdown: breakdown?.products || [],
        subscriptionBreakdown: breakdown?.subscriptions || [],
        addonBreakdown: breakdown?.addons || [],
        otherLineBreakdown: breakdown?.otherLines || [],
        createdAt: new Date().toISOString(),
      })
    } catch (error) {
      console.error('Error creating cash-up:', error)
      const msg = String(error?.message || '')
      if (msg.toLowerCase().includes('already exists')) {
        toast.error(`Cash-up already done for ${form.date}`)
        setActiveTab('history')
      } else {
        toast.error(msg || 'Failed to create cash-up')
      }
    } finally {
      setCreating(false)
    }
  }

  async function printCashUpSlip(cashUpRecord, format) {
    if (!format) {
      setSlipTarget(cashUpRecord)
      return
    }
    setPrintingSlip(true)
    try {
      const businessProfile = await businessProfileService.get()
      let branch = null
      if (cashUpRecord.branchId) {
        try {
          branch = await BranchService.getBranchById(cashUpRecord.branchId)
        } catch (e) {
          console.warn('Could not load branch for cash-up slip:', e)
        }
      }
      const createdAt = cashUpRecord.createdAt?.toDate
        ? cashUpRecord.createdAt.toDate()
        : cashUpRecord.createdAt
      const blob = await PDFGenerator.generateCashUpSlip(
        {
          ...cashUpRecord,
          branchName: cashUpRecord.branchName || branch?.name || '',
          createdAt: createdAt ? new Date(createdAt).toISOString() : new Date().toISOString(),
        },
        businessProfile,
        branch,
        { format }
      )
      const datePart = cashUpRecord.cashUpDate || cashUpRecord.id
      const filename = `cash-up-${format}-${datePart}.pdf`
      if (format === 'thermal') {
        await PDFGenerator.printPdf(blob, { filename, width: 300, height: 700 })
      } else {
        await PDFGenerator.printPdf(blob, { filename, width: 900, height: 1100 })
        PDFGenerator.downloadPDF(blob, filename)
      }
      setSlipTarget(null)
      toast.success(format === 'thermal' ? 'Thermal slip sent to printer' : 'A4 slip ready to print')
    } catch (error) {
      console.error('Error generating cash-up slip:', error)
      toast.error('Failed to generate cash-up slip')
    } finally {
      setPrintingSlip(false)
    }
  }

  async function saveHistoryCounted(cashUpRecord) {
    if (!cashUpRecord?.id) return
    setSavingCounted(true)
    try {
      await cashUpService.updateCountedAmounts({
        cashUpId: cashUpRecord.id,
        countedCash: parseMoneyInput(editCounted.cash),
        countedCard: parseMoneyInput(editCounted.card),
        countedEft: parseMoneyInput(editCounted.eft),
        countedOther: parseMoneyInput(editCounted.other),
        countedByUid: currentUser?.uid,
        countedByName: userProfile?.name || userProfile?.displayName || currentUser?.email,
      })
      toast.success('Counted amounts saved. POS transactions were not changed.')
      setEditingCountedId(null)
    } catch (error) {
      console.error(error)
      toast.error('Could not save counted amounts')
    } finally {
      setSavingCounted(false)
    }
  }

  function startEditCounted(cashUpRecord) {
    const { counted: c } = countedTotalsFromCashUp(cashUpRecord)
    setEditingCountedId(cashUpRecord.id)
    setEditCounted({
      cash: Number(c.cash || 0).toFixed(2),
      card: Number(c.card || 0).toFixed(2),
      eft: Number(c.eft || 0).toFixed(2),
      other: Number(c.other || 0).toFixed(2),
    })
  }

  function selectDate(ymd, { openTab } = {}) {
    setForm((p) => ({ ...p, date: ymd }))
    if (openTab) setActiveTab(openTab)
  }

  const calendarDays = useMemo(() => {
    const start = startOfMonth(calendarMonth)
    const end = endOfMonth(calendarMonth)

    // Make grid start on Monday
    const day = start.getDay() // 0 Sun..6 Sat
    const mondayIndex = (day + 6) % 7
    const gridStart = addDays(start, -mondayIndex)

    const days = []
    for (let d = new Date(gridStart); d <= end || days.length % 7 !== 0; d = addDays(d, 1)) {
      days.push(new Date(d))
      // Stop once we've covered the month and completed the week row
      if (d > end && days.length % 7 === 0) break
    }
    return days
  }, [calendarMonth])

  const filteredHistory = useMemo(() => {
    const selected = form.date
    return cashUps.filter((c) => !selected || c.cashUpDate === selected)
  }, [cashUps, form.date])

  if (!canCashUp) {
    return (
      <div className="mx-auto max-w-5xl space-y-8">
        <PageHeader
          eyebrow="POS"
          title="Cash-ups"
          description="Count the till against POS totals. Transactions stay unchanged."
        />
        <Card className="shadow-sm border-border/80">
          <CardContent className="py-10 text-center space-y-2">
            <h2 className="text-lg font-semibold text-foreground">No cash-up access</h2>
            <p className="text-sm text-muted-foreground">
              Ask an owner to grant the Perform cash-up permission on your role or user.
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-5xl space-y-8">
      <PageHeader
        eyebrow="POS"
        title="Cash-ups"
        description="Count the till against POS totals. Transactions stay unchanged."
      />

      {!currentBranch || isViewingAllBranches() ? (
        <Card className="shadow-sm border-border/80">
          <CardContent className="py-10">
            <p className="text-sm text-muted-foreground">
              Select a specific branch to view cash-up status, history, and create cash-ups.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {missingLast14Days.length > 0 && (
            <Card className="shadow-sm border-border/80">
              <CardHeader>
                <CardTitle className="text-base">Missing cash-ups</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  The following days in the last 14 days have no cash-up saved.
                </p>
                <div className="flex flex-wrap gap-2">
                  {missingLast14Days.map((d) => (
                    <Button key={d} variant="outline" size="sm" onClick={() => selectDate(d, { openTab: 'calendar' })}>
                      {d}
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
            <TabsList>
              <TabsTrigger value="calendar">Calendar</TabsTrigger>
              <TabsTrigger value="cashup">Cash up</TabsTrigger>
              <TabsTrigger value="history">History</TabsTrigger>
            </TabsList>

            <TabsContent value="calendar" className="space-y-4">
              <Card className="shadow-sm border-border/80">
                <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <CardTitle className="text-base">
                    {calendarMonth.toLocaleDateString('en-ZA', { month: 'long', year: 'numeric' })}
                  </CardTitle>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCalendarMonth(startOfMonth(addDays(calendarMonth, -28)))}
                    >
                      Prev
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCalendarMonth(startOfMonth(new Date()))}
                    >
                      Today
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCalendarMonth(startOfMonth(addDays(calendarMonth, 32)))}
                    >
                      Next
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-7 gap-2 text-xs text-muted-foreground mb-2">
                    {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
                      <div key={d} className="px-1 py-1">
                        {d}
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-7 gap-2">
                    {calendarDays.map((d) => {
                      const ymd = toYMD(d)
                      const inMonth = d.getMonth() === calendarMonth.getMonth()
                      const isDone = cashUpsByDate.has(ymd)
                      const isSelected = form.date === ymd
                      return (
                        <button
                          key={ymd}
                          type="button"
                          onClick={() => selectDate(ymd, { openTab: isDone ? 'history' : 'cashup' })}
                          className={[
                            'rounded-md border px-2 py-2 text-left transition-colors',
                            inMonth ? 'bg-card' : 'bg-muted/30 text-muted-foreground',
                            isDone ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-border',
                            !isDone && inMonth ? 'hover:bg-muted/40' : 'hover:bg-emerald-500/10',
                            isSelected ? 'ring-2 ring-primary/30' : '',
                          ].join(' ')}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium">{d.getDate()}</span>
                            <span className="text-[10px]">
                              {isDone ? 'Done' : inMonth ? 'Missing' : ''}
                            </span>
                          </div>
                        </button>
                      )
                    })}
                  </div>
                  <p className="mt-3 text-xs text-muted-foreground">
                    Click a day to view history (Done) or to cash up (Missing).
                  </p>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="cashup" className="space-y-4">
              <Card className="shadow-sm border-border/80">
                <CardHeader>
                  <CardTitle className="text-base">Cash up for {form.date}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="cashUpDate">Cash-up date</Label>
                      <Input
                        id="cashUpDate"
                        type="date"
                        value={form.date}
                        onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Transactions loaded</Label>
                      <div className="h-10 flex items-center rounded-md border border-border bg-muted/30 px-3 text-sm font-medium tabular-nums">
                        {summaryLoading ? 'Loading…' : String(daySummary.transactionCount || 0)}
                      </div>
                    </div>
                  </div>

                  <Card className="border-border/60 shadow-none">
                    <CardHeader className="py-4">
                      <CardTitle className="text-sm font-semibold">POS totals (system)</CardTitle>
                      <p className="text-xs text-muted-foreground font-normal">
                        From completed transactions. These do not change when you count the till.
                      </p>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 text-sm">
                        {[
                          ['Cash', daySummary.cashTotal],
                          ['Card', daySummary.cardTotal],
                          ['EFT', daySummary.eftTotal],
                          ['Other', daySummary.otherTotal],
                        ].map(([label, value]) => (
                          <div
                            key={label}
                            className="flex items-center justify-between rounded-md border border-border/60 bg-muted/20 px-3 py-2"
                          >
                            <span className="text-muted-foreground">{label}</span>
                            <span className="font-medium tabular-nums">{formatMoney(value)}</span>
                          </div>
                        ))}
                        <div
                          className={`flex items-center justify-between rounded-md border px-3 py-2 sm:col-span-2 lg:col-span-4 text-sm ${
                            (daySummary.accountTransactionCount || 0) > 0
                              ? 'border-amber-200/60 bg-amber-50/50'
                              : 'border-border/60 bg-muted/10'
                          }`}
                        >
                          <span className="text-muted-foreground">On account (not in till)</span>
                          <span className={`font-medium tabular-nums ${(daySummary.accountTransactionCount || 0) > 0 ? 'text-amber-900' : ''}`}>
                            {formatMoney(daySummary.accountTotal)} · {daySummary.accountTransactionCount || 0} txn
                          </span>
                        </div>
                      </div>
                      <div className="mt-4 flex items-center justify-between rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">
                        <span className="text-muted-foreground">POS grand total (till)</span>
                        <span className="font-semibold tabular-nums">{formatMoney(grandTotal)}</span>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-border/60 shadow-none">
                    <CardHeader className="py-4">
                      <CardTitle className="text-sm font-semibold">Counted (physical check)</CardTitle>
                      <p className="text-xs text-muted-foreground font-normal">
                        Enter what you counted. This is a check only — it does not change any POS sale.
                      </p>
                    </CardHeader>
                    <CardContent className="space-y-3 pt-0">
                      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                        {[
                          ['cash', 'Cash', daySummary.cashTotal],
                          ['card', 'Card', daySummary.cardTotal],
                          ['eft', 'EFT', daySummary.eftTotal],
                          ['other', 'Other', daySummary.otherTotal],
                        ].map(([key, label, expected]) => {
                          const countedVal = parseMoneyInput(counted[key])
                          const diff = variance(countedVal, expected)
                          return (
                            <div key={key} className="space-y-1.5">
                              <Label htmlFor={`counted-${key}`}>{label} counted</Label>
                              <Input
                                id={`counted-${key}`}
                                type="number"
                                inputMode="decimal"
                                step="0.01"
                                min="0"
                                value={counted[key]}
                                onChange={(e) =>
                                  setCounted((p) => ({ ...p, [key]: e.target.value }))
                                }
                                className="tabular-nums"
                              />
                              <p className={`text-xs tabular-nums ${varianceClass(diff)}`}>
                                Variance {formatVariance(diff)}
                              </p>
                            </div>
                          )
                        })}
                      </div>
                      <div className="flex items-center justify-between rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">
                        <span className="text-muted-foreground">Counted grand total</span>
                        <span className="font-semibold tabular-nums">{formatMoney(countedGrand)}</span>
                      </div>
                      <div className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm">
                        <span className="text-muted-foreground">Overall variance</span>
                        <span className={`font-semibold tabular-nums ${varianceClass(countedGrand - grandTotal)}`}>
                          {formatVariance(countedGrand - grandTotal)}
                        </span>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-border/60 shadow-none">
                    <CardHeader className="py-4">
                      <CardTitle className="text-sm font-semibold">Items sold</CardTitle>
                      <p className="text-xs text-muted-foreground font-normal">
                        Wash types from the bay queue, plus products, add-ons, and other POS lines for this day.
                        Saved with the cash-up.
                      </p>
                    </CardHeader>
                    <CardContent>
                      {summaryLoading ? (
                        <p className="text-muted-foreground text-sm">Loading…</p>
                      ) : (
                        <SalesBreakdownTables breakdown={previewBreakdown} />
                      )}
                    </CardContent>
                  </Card>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="emailTo">Email slip to</Label>
                      <Input
                        id="emailTo"
                        name="emailTo"
                        type="email"
                        value={form.emailTo}
                        onChange={(e) => setForm((p) => ({ ...p, emailTo: e.target.value }))}
                        placeholder="owner@company.co.za"
                      />
                      <p className="text-xs text-muted-foreground">
                        This will open your mail app and remind you where to reprint.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label>Counted grand total</Label>
                      <div className="h-10 flex items-center justify-between rounded-md border border-border bg-muted/30 px-3 text-sm font-medium tabular-nums">
                        <span>{formatMoney(countedGrand)}</span>
                        <span className={`text-xs ${varianceClass(countedGrand - grandTotal)}`}>
                          {formatVariance(countedGrand - grandTotal)} vs POS
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="notes">Notes</Label>
                    <Input
                      id="notes"
                      value={form.notes}
                      onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
                      placeholder="Optional"
                    />
                  </div>

                  <div className="flex justify-between gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setActiveTab('calendar')}
                    >
                      Back to calendar
                    </Button>
                    <Button onClick={createCashUp} disabled={creating || summaryLoading}>
                      {creating ? (
                        <>
                          <LoadingSpinner size="small" className="mr-2" />
                          Creating…
                        </>
                      ) : (
                        'Cash up (save)'
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card className="shadow-sm border-border/80">
                <CardHeader>
                  <CardTitle className="text-base">POS transactions for {form.date}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {summaryLoading ? (
                    <div className="flex h-24 items-center justify-center">
                      <LoadingSpinner />
                    </div>
                  ) : dayTransactions.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No POS transactions found for this day.</p>
                  ) : (
                    dayTransactions.slice(0, 50).map((t) => (
                      <div
                        key={`${t._source || 'src'}-${t.id}`}
                        className="flex flex-col gap-1 rounded-lg border border-border/70 p-3 sm:flex-row sm:items-center sm:justify-between"
                      >
                        <div>
                          <p className="text-sm font-medium text-foreground">
                            {t.customerName || 'Walk-in'} · {String(t.paymentMethod || 'N/A').toUpperCase()}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {t._source || 'POS'} · {t.id}
                          </p>
                        </div>
                        <div className="text-sm font-semibold tabular-nums text-foreground">
                          {formatMoney(t.total ?? t.amount ?? 0)}
                        </div>
                      </div>
                    ))
                  )}
                  {dayTransactions.length > 50 && (
                    <p className="text-xs text-muted-foreground">Showing first 50 transactions.</p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="history" className="space-y-4">
              <Card className="shadow-sm border-border/80">
                <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <CardTitle className="text-base">History</CardTitle>
                  <div className="flex items-center gap-2">
                    <Label className="text-xs text-muted-foreground">Filter by date</Label>
                    <Input
                      type="date"
                      value={form.date}
                      onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))}
                      className="h-9 w-[160px]"
                    />
                    <Button variant="outline" size="sm" onClick={() => setForm((p) => ({ ...p, date: toYMD(new Date()) }))}>
                      Today
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {loading ? (
                    <div className="flex h-24 items-center justify-center">
                      <LoadingSpinner />
                    </div>
                  ) : cashUps.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No cash-ups yet.</p>
                  ) : filteredHistory.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      No cash-up found for {form.date}. Use the calendar tab to cash up this day.
                    </p>
                  ) : (
                    filteredHistory.map((c) => {
                      const { expected, counted: countedVals } = countedTotalsFromCashUp(c)
                      const posTotal = tillGrandFrom(expected)
                      const countedTotal = tillGrandFrom(countedVals)
                      const diff = countedTotal - posTotal
                      const created = c.createdAt?.toDate ? c.createdAt.toDate() : null
                      const isEditing = editingCountedId === c.id
                      return (
                        <div
                          key={c.id}
                          className="flex flex-col gap-3 rounded-lg border border-border/80 p-4"
                        >
                          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                            <div>
                              <p className="text-sm font-medium text-foreground">
                                {c.cashUpDate || 'N/A'} · POS {formatMoney(posTotal)} · Counted{' '}
                                {formatMoney(countedTotal)}
                              </p>
                              <p className={`text-xs tabular-nums ${varianceClass(diff)}`}>
                                Variance {formatVariance(diff)}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {c.branchName || c.branchId || 'Branch'} ·{' '}
                                {created ? created.toLocaleString('en-ZA') : 'N/A'} · {c.createdByName || 'N/A'}
                              </p>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  isEditing ? setEditingCountedId(null) : startEditCounted(c)
                                }
                              >
                                {isEditing ? 'Cancel' : 'Edit counted'}
                              </Button>
                              <Button variant="outline" size="sm" onClick={() => printCashUpSlip(c)}>
                                Print slip
                              </Button>
                            </div>
                          </div>
                          {isEditing && (
                            <div className="space-y-3 rounded-md border border-border/70 bg-muted/20 p-3">
                              <p className="text-xs text-muted-foreground">
                                POS totals stay as recorded. Update only what was physically counted.
                              </p>
                              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                                {[
                                  ['cash', 'Cash', expected.cash],
                                  ['card', 'Card', expected.card],
                                  ['eft', 'EFT', expected.eft],
                                  ['other', 'Other', expected.other],
                                ].map(([key, label, posVal]) => {
                                  const countedVal = parseMoneyInput(editCounted[key])
                                  const rowDiff = variance(countedVal, posVal)
                                  return (
                                    <div key={key} className="space-y-1">
                                      <Label htmlFor={`hist-${c.id}-${key}`}>
                                        {label} counted
                                      </Label>
                                      <p className="text-[11px] text-muted-foreground tabular-nums">
                                        POS {formatMoney(posVal)}
                                      </p>
                                      <Input
                                        id={`hist-${c.id}-${key}`}
                                        type="number"
                                        inputMode="decimal"
                                        step="0.01"
                                        min="0"
                                        value={editCounted[key]}
                                        onChange={(e) =>
                                          setEditCounted((p) => ({ ...p, [key]: e.target.value }))
                                        }
                                        className="tabular-nums"
                                      />
                                      <p className={`text-xs tabular-nums ${varianceClass(rowDiff)}`}>
                                        {formatVariance(rowDiff)}
                                      </p>
                                    </div>
                                  )
                                })}
                              </div>
                              <div className="flex justify-end">
                                <Button
                                  size="sm"
                                  disabled={savingCounted}
                                  onClick={() => saveHistoryCounted(c)}
                                >
                                  {savingCounted ? 'Saving…' : 'Save counted amounts'}
                                </Button>
                              </div>
                            </div>
                          )}
                          <div className="rounded-md border border-border/60 bg-muted/10 p-3">
                            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                              Items sold
                            </p>
                            <SalesBreakdownTables
                              breakdown={cashUpBreakdownFromRecord(c)}
                              emptyHint="No item breakdown saved on this cash-up."
                            />
                          </div>
                        </div>
                      )
                    })
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </>
      )}

      {slipTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <Card className="w-full max-w-md shadow-xl">
            <CardHeader>
              <CardTitle className="text-base">Print cash-up slip</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                What size do you need for {slipTarget.cashUpDate || 'this cash-up'}?
              </p>
              <Button
                className="w-full"
                disabled={printingSlip}
                onClick={() => printCashUpSlip(slipTarget, 'a4')}
              >
                {printingSlip ? 'Preparing…' : 'A4'}
              </Button>
              <Button
                className="w-full"
                variant="secondary"
                disabled={printingSlip}
                onClick={() => printCashUpSlip(slipTarget, 'thermal')}
              >
                {printingSlip ? 'Preparing…' : 'Thermal slip (80mm)'}
              </Button>
              <Button
                className="w-full"
                variant="ghost"
                disabled={printingSlip}
                onClick={() => setSlipTarget(null)}
              >
                Cancel
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}

